import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, REQUEST } from '@angular/core';
import { map, Observable } from 'rxjs';

import { API_URL } from '../api-url';
import { resolveReferences } from './jsog';

/**
 * The single door to unimarket-api.
 *
 * A storefront is picked by request host, so while rendering on the server the browser's host is
 * forwarded and the visitor's cookies travel with the call; that is what keeps the server-rendered
 * cart and the browser's cart the same cart.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {

  private http = inject(HttpClient);
  private request = inject(REQUEST, { optional: true });
  private baseUrl = inject(API_URL);

  get<T>(path: string): Observable<T> {
    return this.resolved(this.http.get<T>(this.url(path), this.options()));
  }

  post<T>(path: string, body: unknown = null): Observable<T> {
    return this.resolved(this.http.post<T>(this.url(path), body, this.options()));
  }

  put<T>(path: string, body: unknown): Observable<T> {
    return this.resolved(this.http.put<T>(this.url(path), body, this.options()));
  }

  patch<T>(path: string, body: unknown): Observable<T> {
    return this.resolved(this.http.patch<T>(this.url(path), body, this.options()));
  }

  delete<T>(path: string): Observable<T> {
    return this.resolved(this.http.delete<T>(this.url(path), this.options()));
  }

  private resolved<T>(response: Observable<T>): Observable<T> {
    return response.pipe(map(body => resolveReferences(body)));
  }

  private url(path: string) {
    return this.baseUrl + '/api' + path;
  }

  private options() {
    return { headers: this.headers(), withCredentials: true };
  }

  private headers() {
    if (!this.request) {
      return new HttpHeaders();
    }
    const url = new URL(this.request.url);
    let headers = new HttpHeaders()
      .set('X-Forwarded-Host', this.request.headers.get('host') ?? url.host)
      .set('X-Forwarded-Proto', url.protocol.replace(':', ''));
    const cookie = this.request.headers.get('cookie');
    if (cookie) {
      headers = headers.set('Cookie', cookie);
    }
    return headers;
  }

}
